import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Layout } from 'antd';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import AdminLayout from '@/components/layout/AdminLayout';
import AuthGuard from '@/components/auth/AuthGuard';
import AuthProvider from '@/components/auth/AuthProvider';
import { BreadcrumbProvider } from '@/contexts/BreadcrumbContext';
import Home from '@/pages/Home';
import ProductDetail from '@/pages/ProductDetail';
import Cart from '@/pages/Cart';
import Checkout from '@/pages/Checkout';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import VerifyEmail from '@/pages/VerifyEmail';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Profile from '@/pages/Profile';
import Wallet from '@/pages/Wallet';
// Subscription pages hidden — see GitHub #24 (replaced by wallet). Files retained for possible revival.
// import SubscriptionManagement from '@/pages/SubscriptionManagement';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import UserManagement from '@/pages/admin/UserManagement';
import QuotationManagement from '@/pages/admin/QuotationManagement';
import ProductManagement from '@/pages/admin/ProductManagement';
import CategoryManagement from '@/pages/admin/CategoryManagement';
import BillingDashboard from '@/pages/admin/BillingDashboard';
import TransactionList from '@/pages/admin/TransactionList';
import TransactionDetail from '@/pages/admin/TransactionDetail';
import InvoiceList from '@/pages/admin/InvoiceList';
// import SubscriptionList from '@/pages/admin/SubscriptionList'; // hidden — see #24
import WalletTierManagement from '@/pages/admin/WalletTierManagement';
import PromoCodeManagement from '@/pages/admin/PromoCodeManagement';
// CustomerWalletList removed — wallet history now lives inside the User Management detail modal.
import PaymentAnalytics from '@/pages/admin/PaymentAnalytics';
import Settings from '@/pages/admin/Settings';
import EmailTemplateManagement from '@/pages/admin/EmailTemplateManagement';
import UserGuide from '@/pages/admin/UserGuide';
import NotFound from '@/pages/NotFound';
import './App.css';

const { Content } = Layout;

const ProductsRedirect: React.FC = () => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/', search }} replace />;
};

const App: React.FC = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Reset scroll to the top on every route change. Without this, React Router
  // preserves the window scroll position, so navigating from a long list (e.g.
  // Products) to a shorter page (Product Detail) left the viewport at the bottom.
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <AuthProvider>
      <BreadcrumbProvider>
        <Layout className="min-h-screen">
          {!isAdminRoute && <Header />}
          <Content className="flex-1">
          <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          {/* Home and Products are one page (issue #189). Old links, including
              their search and category, land on it. */}
          <Route path="/products" element={<ProductsRedirect />} />
          <Route path="/products/:supersedeNo/:uom" element={<ProductDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Routes that allow guest access */}
          <Route 
            path="/cart" 
            element={
              <AuthGuard allowGuest>
                <Cart />
              </AuthGuard>
            } 
          />
          <Route 
            path="/checkout" 
            element={
              <AuthGuard allowGuest>
                <Checkout />
              </AuthGuard>
            } 
          />
          
          {/* Protected routes (require authenticated user) */}
          <Route 
            path="/profile" 
            element={
              <AuthGuard>
                <Profile />
              </AuthGuard>
            } 
          />
          <Route
            path="/settings"
            element={
              <AuthGuard>
                <Navigate to="/profile?tab=settings" replace />
              </AuthGuard>
            }
          />
          {/* /subscription route hidden — see #24 (wallet replaces subscriptions) */}
          <Route
            path="/wallet"
            element={
              <AuthGuard>
                <Wallet />
              </AuthGuard>
            }
          />

          {/* Admin routes (require admin role) */}
          <Route 
            path="/admin" 
            element={
              <AuthGuard requireAdmin>
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              </AuthGuard>
            } 
          />
          <Route
            path="/admin/users"
            element={
              <AuthGuard requireAdmin>
                <AdminLayout>
                  <UserManagement />
                </AdminLayout>
              </AuthGuard>
            }
          />
          {/* Inactive customers moved into a User Management tab; keep the old
              path working by redirecting to it. */}
          <Route
            path="/admin/customers/inactive"
            element={<Navigate to="/admin/users?tab=inactive" replace />}
          />
          <Route
            path="/admin/quotations"
            element={
              <AuthGuard requireAdmin>
                <AdminLayout>
                  <QuotationManagement />
                </AdminLayout>
              </AuthGuard>
            } 
          />
          <Route
            path="/admin/products"
            element={
              <AuthGuard requireAdmin>
                <AdminLayout>
                  <ProductManagement />
                </AdminLayout>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/categories"
            element={
              <AuthGuard requireAdmin>
                <AdminLayout>
                  <CategoryManagement />
                </AdminLayout>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/billing"
            element={
              <AuthGuard requireAdmin>
                <AdminLayout>
                  <BillingDashboard />
                </AdminLayout>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/billing/transactions"
            element={
              <AuthGuard requireAdmin>
                <AdminLayout>
                  <TransactionList />
                </AdminLayout>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/billing/transactions/:id"
            element={
              <AuthGuard requireAdmin>
                <AdminLayout>
                  <TransactionDetail />
                </AdminLayout>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/billing/invoices"
            element={
              <AuthGuard requireAdmin>
                <AdminLayout>
                  <InvoiceList />
                </AdminLayout>
              </AuthGuard>
            }
          />
          {/* /admin/billing/subscriptions route hidden — see #24 */}
          <Route
            path="/admin/wallet/tiers"
            element={
              <AuthGuard requireAdmin>
                <AdminLayout>
                  <WalletTierManagement />
                </AdminLayout>
              </AuthGuard>
            }
          />
          {/* /admin/wallet/customers removed — wallet history now lives in User Management view */}
          <Route
            path="/admin/promo-codes"
            element={
              <AuthGuard requireAdmin>
                <AdminLayout>
                  <PromoCodeManagement />
                </AdminLayout>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/billing/analytics"
            element={
              <AuthGuard requireAdmin>
                <AdminLayout>
                  <PaymentAnalytics />
                </AdminLayout>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <AuthGuard requireAdmin>
                <AdminLayout>
                  <Settings />
                </AdminLayout>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/email-templates"
            element={
              <AuthGuard requireAdmin>
                <AdminLayout>
                  <EmailTemplateManagement />
                </AdminLayout>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/user-guide"
            element={
              <AuthGuard requireAdmin>
                <AdminLayout>
                  <UserGuide />
                </AdminLayout>
              </AuthGuard>
            }
          />

          {/* 404 route */}
          <Route path="*" element={<NotFound />} />
          </Routes>
        </Content>
        {!isAdminRoute && <Footer />}
      </Layout>
      </BreadcrumbProvider>
    </AuthProvider>
  );
};

export default App;